import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Coins, Plus, Trash2 } from "lucide-react";
import { useDespachos } from "@/hooks/useDespachos";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

interface AdvanceItem {
    id: string;
    title: string;
    amount: number;
    date: string;
    status: string;
    case_number?: string;
}

export const ClientAdvances = ({ client }: { client: any }) => {
    const { expenses, processes, addExpense, updateClient } = useDespachos();
    const [isOpen, setIsOpen] = useState(false);
    const [title, setTitle] = useState("");
    const [amount, setAmount] = useState("");
    const [date, setDate] = useState(new Date().toISOString().split("T")[0]);

    if (!client) {
        return (
            <Card>
                <CardContent className="p-8 text-center text-gray-500">Cargando...</CardContent>
            </Card>
        );
    }

    // 1. Obtener los datos del address serializado
    let extraData: any = {};
    if (client.address) {
        try {
            extraData = JSON.parse(client.address);
        } catch (e) {
            extraData = {};
        }
    }
    const jsonAdvances: AdvanceItem[] = extraData.advances || [];

    // 2. Obtener todos los expedientes de este cliente
    const clientProcesses = processes.filter(
        p => (p.client_name && p.client_name.trim().toLowerCase() === client.name?.trim().toLowerCase()) ||
             (p.metadata && p.metadata.client_id === client.id)
    );
    const clientProcessIds = clientProcesses.map(p => p.id);

    // 3. Gastos de la BD asociados
    const dbExpenses: AdvanceItem[] = expenses
        .filter(e => clientProcessIds.includes(e.case_id))
        .map(e => {
            const rel = clientProcesses.find(p => p.id === e.case_id);
            return {
                id: e.id,
                title: e.title,
                amount: e.amount,
                date: e.date,
                status: e.status,
                case_number: rel?.case_number || "Gasto de expediente"
            };
        });

    // 4. Fusionar gastos de BD y de JSON
    const mergedMap = new Map();
    dbExpenses.forEach(item => mergedMap.set(item.id, item));
    jsonAdvances.forEach(item => {
        if (!mergedMap.has(item.id)) {
            mergedMap.set(item.id, item);
        }
    });
    const clientExpenses: AdvanceItem[] = Array.from(mergedMap.values());

    const handleCreateAdvance = async () => {
        if (!title.trim() || !amount.trim()) return;

        const numAmount = parseFloat(amount) || 0;
        const newAdvance: AdvanceItem = {
            id: Math.random().toString(36).substring(2, 9),
            title: title.trim(),
            amount: numAmount,
            date: date || new Date().toISOString().split("T")[0],
            status: "pending",
            case_number: clientProcesses[0]?.case_number || "Directo Cliente"
        };

        // Si tiene expediente, registrar también en legal_expenses de BD
        if (clientProcesses.length > 0) {
            try {
                await addExpense({
                    case_id: clientProcesses[0].id,
                    title: `[ADELANTO] ${title.trim()}`,
                    amount: numAmount,
                    date: date,
                    is_reimbursable: true,
                    status: "pending"
                });
            } catch (e) {
                console.error("Error saving expense to DB:", e);
            }
        }

        // Persistir en JSON dentro de client.address
        const updatedExtra = {
            ...extraData,
            advances: [...jsonAdvances, newAdvance]
        };
        await updateClient(client.id, {
            address: JSON.stringify(updatedExtra)
        });

        // Limpiar form
        setTitle("");
        setAmount("");
        setIsOpen(false);
    };

    const handleDeleteAdvance = async (advanceId: string) => {
        const updatedAdvances = jsonAdvances.filter(a => a.id !== advanceId);
        const updatedExtra = {
            ...extraData,
            advances: updatedAdvances
        };
        await updateClient(client.id, {
            address: JSON.stringify(updatedExtra)
        });
    };

    const formatCurrency = (val: number) => {
        return new Intl.NumberFormat("es-CO", {
            style: "currency",
            currency: "COP",
            minimumFractionDigits: 0
        }).format(val);
    };

    return (
        <Card className="border border-gray-100/80 shadow-sm overflow-hidden bg-white">
            <CardHeader className="flex flex-row items-center justify-between pb-3 bg-gray-50/50 border-b border-gray-100">
                <div>
                    <CardTitle className="text-base font-bold text-gray-900">Historial de Provisiones y Gastos</CardTitle>
                    <p className="text-xs text-gray-400">Fondos anticipados y consumos registrados para este cliente</p>
                </div>
                <Dialog open={isOpen} onOpenChange={setIsOpen}>
                    <DialogTrigger asChild>
                        <Button size="sm" className="rounded-xl bg-legal-blue hover:bg-legal-navy gap-1.5 shadow-md">
                            <Plus className="h-4 w-4" /> Registrar Adelanto
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="rounded-2xl">
                        <DialogHeader>
                            <DialogTitle className="text-gray-900 font-bold">Registrar Nuevo Adelanto / Fondo</DialogTitle>
                            <p className="text-xs text-gray-400">Registra un depósito o provisión entregada por el cliente</p>
                        </DialogHeader>
                        <div className="py-4 space-y-4">
                            <div className="space-y-2">
                                <Label>Concepto / Título *</Label>
                                <Input className="rounded-xl" placeholder="Ej: Provisión para gastos de expediente" value={title} onChange={e => setTitle(e.target.value)} />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Monto ($) *</Label>
                                    <Input className="rounded-xl" placeholder="500000" value={amount} onChange={e => setAmount(e.target.value)} />
                                </div>
                                <div className="space-y-2">
                                    <Label>Fecha</Label>
                                    <Input type="date" className="rounded-xl" value={date} onChange={e => setDate(e.target.value)} />
                                </div>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="ghost" className="rounded-xl" onClick={() => setIsOpen(false)}>Cancelar</Button>
                            <Button className="rounded-xl bg-legal-blue" onClick={handleCreateAdvance} disabled={!title.trim() || !amount.trim()}>Registrar</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </CardHeader>
            <CardContent className="p-0">
                {clientExpenses.length > 0 ? (
                    <Table>
                        <TableHeader>
                            <TableRow className="border-gray-100 hover:bg-transparent">
                                <TableHead className="font-semibold text-gray-500">Expediente / Ref</TableHead>
                                <TableHead className="font-semibold text-gray-500">Concepto</TableHead>
                                <TableHead className="font-semibold text-gray-500">Fecha</TableHead>
                                <TableHead className="font-semibold text-gray-500">Estado</TableHead>
                                <TableHead className="font-semibold text-gray-500 text-right">Valor</TableHead>
                                <TableHead className="font-semibold text-gray-500 text-right">Acción</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {clientExpenses.map((e) => (
                                <TableRow key={e.id} className="border-gray-100 hover:bg-blue-50/20 transition-colors">
                                    <TableCell className="font-bold text-gray-800 text-xs">
                                        {e.case_number || "Directo"}
                                    </TableCell>
                                    <TableCell className="font-semibold text-gray-700 text-xs">{e.title}</TableCell>
                                    <TableCell className="text-gray-500 text-xs">
                                        {e.date ? new Date(e.date).toLocaleDateString() : "—"}
                                    </TableCell>
                                    <TableCell>
                                        <span className={`text-[10px] font-bold py-0.5 px-2 rounded-full border ${
                                            e.status === 'completed' || e.status === 'reimbursed'
                                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                                : 'bg-amber-50 text-amber-700 border-amber-200'
                                        }`}>
                                            {e.status === 'completed' || e.status === 'reimbursed' ? 'Reembolsado' : 'Registrado'}
                                        </span>
                                    </TableCell>
                                    <TableCell className="text-right font-bold text-gray-900 text-xs">
                                        {formatCurrency(e.amount)}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => handleDeleteAdvance(e.id)}
                                            className="h-8 w-8 p-0 rounded-lg text-red-500 hover:text-red-600 hover:bg-red-50"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                ) : (
                    <div className="text-center py-16 text-gray-400">
                        <Coins className="h-10 w-10 text-gray-200 mx-auto mb-3" />
                        <p className="text-sm">No hay adelantos o provisiones financieras registradas.</p>
                        <p className="text-xs text-gray-300 mt-1">Usa el botón superior para registrar un adelanto para este cliente.</p>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};
